import { _isNotEmpty, _notNil } from '@/littledash';
import type { SendResultReport, SendRunScope, SendVerdictTally } from '@/model/Agent.model';
import type { FC } from 'react';
import SendCapabilityLines from './SendCapabilityLines';
import SendCoverageGrid from './SendCoverageGrid';
import SendRuleId from './SendRuleId';
import { ratioText } from '@/components/Agents/SendValidator/utils/SendRatio';
import { partialRulesOf } from '@/components/Agents/SendValidator/utils/SendRuleOutcomes';
import { capabilityOf } from '@/components/Agents/SendValidator/utils/SendStoredReport';
import { terminologyText } from '@/components/Agents/SendValidator/utils/SendScope';
import SendTierGrid from './SendTierGrid';
import styles from './SendResults.module.scss';

interface SendRuleChecksProps {
  report: SendResultReport;
  tally: SendVerdictTally;
  /** The run's scope, which says which terminology release it used; absent from results stored before it. */
  scope?: SendRunScope;
  testId?: string;
}

/**
 * The releases a run was checked against. The terminology row says which release the run used, beside
 * what the study declared and what is bundled; an older result only names the bundled default.
 */
const versionsOf = (
  report: SendResultReport,
  scope: SendRunScope | undefined
): Array<{ key: string; label: string; value: string }> => [
  { key: 'sendig', label: 'SENDIG', value: report.versions.sendig_version },
  {
    key: 'terminology',
    label: 'Controlled terminology',
    value: _notNil(scope) ? terminologyText(scope) : report.versions.controlled_terminology_version,
  },
  { key: 'register', label: 'Conformance rules', value: report.versions.register_version },
  { key: 'catalogue', label: 'Rule catalogue', value: `release ${report.versions.catalogue_version}` },
];

const buckets: Array<{ key: keyof SendVerdictTally; label: string }> = [
  { key: 'passed', label: 'Passed' },
  { key: 'failed', label: 'Failed' },
  { key: 'not_applicable', label: 'Not applicable' },
  { key: 'not_evaluated', label: 'Not evaluated' },
];

/**
 * How the upload fared against the published SEND rules: the server's tally, as given, with how many
 * of its passes were not an exact, finished check; what this profile can check at all, every share
 * with its denominator; which releases the run was checked against; how the rules landed for each
 * dataset; and the rules whose check covers only part of the rule, each with why. A deterministic
 * account, so it carries no Benchling In Vivo AI label.
 */
const SendRuleChecks: FC<SendRuleChecksProps> = ({
  report,
  tally,
  scope,
  testId = 'invivo-send-validator__rule-checks',
}) => {
  const partial = partialRulesOf(report);
  const { coverage, accounting } = report;
  const capability = capabilityOf(report);
  return (
    <div className="mt3" data-test-component="SendRuleChecks" data-testid={testId}>
      <h3 className={`${styles.sectionTitle} mb1`}>Rule checks</h3>
      <p className="f6 dark-gray ma0 mb2" data-test-element="rule-checks-scope">
        {tally.total} published SEND conformance rules
      </p>
      <p className="f6 dark-gray ma0 mb2" data-test-element="rule-checks-coverage">
        This upload type ({coverage.validation_profile}) can check {ratioText(coverage.exact, coverage.published)} rules
        exactly, and {ratioText(coverage.with_any_check, coverage.published)} at all.{' '}
        {/* A count, never a share: "312 of 312 (100%)" would read as complete coverage, however many of those
            rules the run could not answer. */}
        {accounting.accounted_for} of {accounting.published} published rules have a row in this report, each saying what
        happened to it.
      </p>
      {_notNil(capability) && <SendCapabilityLines capability={capability} />}
      <ul className={styles.tally} data-test-element="rule-checks-tally">
        {buckets.map(({ key, label }) => (
          <li key={key} className={styles.tallyTile} data-test-element="rule-checks-bucket" data-test-key={key}>
            <span className={styles.tallyValue}>{tally[key]}</span>
            <span className={styles.tallyLabel}>{label}</span>
            {key === 'passed' && tally.passed > tally.conforming && (
              <span className={`${styles.tallyLabel} ml1`} data-test-element="rule-checks-passed-in-part">
                ({tally.passed - tally.conforming} checked only in part)
              </span>
            )}
          </li>
        ))}
      </ul>
      <dl className={styles.versions} data-test-element="rule-checks-versions">
        {versionsOf(report, scope).map(({ key, label, value }) => (
          <div key={key} className={styles.version} data-test-key={key}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <SendTierGrid
        rows={report.coverage_grid.by_tier}
        registerRules={report.coverage_grid.register_rules ?? tally.total}
        tierEightChecks={capability?.in_house_checks.by_tier.find((tier) => tier.tier === 'tier_8')?.rules}
      />
      <SendCoverageGrid rows={report.coverage_grid.by_domain} />
      {_isNotEmpty(partial) && (
        <div className="mt3" data-test-element="rule-checks-partial">
          <p className="f6 fw5 ma0 mb2">
            {partial.length} {partial.length === 1 ? 'rule was' : 'rules were'} checked only partially
          </p>
          <ul className="list pa0 ma0">
            {partial.map((rule) => (
              <li
                key={rule.ruleId}
                className="flex items-center mb2"
                data-test-element="rule-checks-partial-rule"
                data-test-key={rule.ruleId}
              >
                <SendRuleId id={rule.ruleId} publisher="cdisc" partial className="mr2" />
                <span className="f6 dark-gray">{rule.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SendRuleChecks;
